import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FiPackage, FiClock, FiCheck } from 'react-icons/fi';
import axiosInstance from '../../api/axios';
import { getOrderItemQrImage } from '../../api/adminBubbleTeaOptions';
import { formatCurrency } from '../../utils/currency';
import { getStatusLabel, formatQueueNumberValue } from '../../utils/orderStatus';

// ─── Types ────────────────────────────────────────────────────────────────────

interface QBOrderItem {
  // The order line item's own id (BubbleTeaOrderItem.bt_order_item_id on the
  // backend, see QBOrderItemSummary.id in backend/app/schemas/bubbletea.py).
  // Needed to look up this specific line item's machine-scan combo QR
  // (task-032-impl-7) -- distinct from menu_item_id, which identifies the
  // catalog product, not the order line.
  id: number;
  menu_item_id: number;
  menu_item_name: string | null;
  quantity: number;
  unit_price: number;
  subtotal: number;
  options?: Record<string, string> | null;
}

interface QBOrder {
  id: number;
  queue_number: number | null;
  customer_name: string | null;
  customer_phone: string | null;
  items: QBOrderItem[];
  total: number;
  status: string;
  created_at: string;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

// Statuses that still require staff action. GET /admin/quick-bites/orders
// defaults to skip=0/limit=100 newest-first (see backend/app/api/admin.py's
// get_qb_orders), so on a dev/prod DB with 100+ historical orders, an older
// pending/preparing/ready order can silently scroll out of the default
// window with zero staff-facing indication (task-032 QA finding). Rather
// than build a full pagination UI, fetch each actionable status separately
// with a generous-but-finite cap and merge the results in with the default
// page, so an old-but-still-actionable order is never invisible.
const ACTIONABLE_STATUSES = ['pending', 'preparing', 'ready'] as const;
// 1000 mirrors the existing limit=1000 stopgap-ceiling precedent already
// used in frontend/src/api/admin.ts's adminProducts.getAll for the same
// reason -- a generous but still-finite cap, not real pagination. If actual
// actionable-order volume ever approaches this, a real paginated/filtered
// UI is the correct next step, not a bigger number here.
const ACTIONABLE_FETCH_LIMIT = 1000;

const fetchQBOrders = async (): Promise<QBOrder[]> => {
  const [defaultRes, ...actionableRes] = await Promise.all([
    axiosInstance.get('/admin/quick-bites/orders'),
    ...ACTIONABLE_STATUSES.map(status =>
      axiosInstance.get('/admin/quick-bites/orders', {
        params: { status, limit: ACTIONABLE_FETCH_LIMIT },
      })
    ),
  ]);

  const merged = new Map<number, QBOrder>();
  [defaultRes, ...actionableRes].forEach(res => {
    (res.data as QBOrder[]).forEach(order => merged.set(order.id, order));
  });

  return Array.from(merged.values()).sort((a, b) => {
    const dateDiff = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    if (dateDiff !== 0) return dateDiff;
    return b.id - a.id;
  });
};

const updateQBOrderStatus = async ({ id, status }: { id: number; status: string }) => {
  const response = await axiosInstance.put(`/admin/quick-bites/orders/${id}/status`, { status });
  return response.data;
};

// ─── Styled Components ────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Header = styled.div`
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const Table = styled.table`
  width: 100%;
  background: #F5F0E8;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: #1C1511;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: #FFFFFF;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const Tr = styled.tr`
  border-bottom: 1px solid #E8DDD0;

  &:hover {
    background: #ede8df;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: #1C1511;
`;

const QueueBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: #D4A017;
  color: #1C1511;
  border-radius: 50%;
  font-weight: 700;
  font-size: 14px;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'pending': return '#fef3c7';
      case 'preparing': return '#e0e7ff';
      case 'ready': return '#d1fae5';
      case 'completed': return '#d1fae5';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch (props.status?.toLowerCase()) {
      case 'pending': return '#92400e';
      case 'preparing': return '#3730a3';
      case 'ready': return '#065f46';
      case 'completed': return '#065f46';
      default: return '#6b7280';
    }
  }};
`;

const StatusSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 13px;
  margin-right: 8px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const UpdateButton = styled.button`
  padding: 6px 12px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
  background: #F5F0E8;
  border-radius: 12px;
`;

// Machine-scan combo QR cell (task-032-impl-7). Deliberately kept
// thumbnail-sized/visually secondary next to the existing options-text
// rendering so the pre-existing Items column layout (journey J24) doesn't get
// crowded -- this is a distinct, additive concept from the customer-facing
// per-item QR elsewhere in the admin UI (QrCodeField.tsx/AdminQBMenu.tsx),
// hence the explicit "Scan into drink machine" label.
const OrderItemQrWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-top: 4px;
`;

const OrderItemQrLabel = styled.span`
  font-size: 10px;
  color: #6B5E4E;
  font-style: italic;
`;

const OrderItemQrImage = styled.img`
  width: 48px;
  height: 48px;
  border: 1px solid #E8DDD0;
  border-radius: 4px;
  background: #FFFFFF;
  object-fit: contain;
`;

const OrderItemQrPlaceholder = styled.span`
  display: inline-block;
  font-size: 10px;
  color: #6B5E4E;
  font-style: italic;
  margin-top: 4px;
`;

// ─── Order item QR cell ───────────────────────────────────────────────────────

interface OrderItemQrCellProps {
  orderId: number;
  itemId: number;
}

// Fetches this order line item's machine-scan combo QR image, mirroring
// AdminQBMenu.tsx's existing fetch-blob-build-object-URL-cleanup-on-unmount
// lifecycle for the customer-facing QrCodeField (see .orchestrator/context.md's
// Discovered section for that established convention) -- kept as its own
// component here rather than reusing/importing QrCodeField, since that
// component is a whole enable/refresh form field, not a compact read-only
// per-line-item thumbnail.
const OrderItemQrCell: React.FC<OrderItemQrCellProps> = ({ orderId, itemId }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'loading' | 'ready' | 'none' | 'error'>('loading');
  const [imageSrc, setImageSrc] = useState('');

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    const loadQrImage = async () => {
      setStatus('loading');
      setImageSrc('');
      try {
        const blob = await getOrderItemQrImage(orderId, itemId);
        if (cancelled) return;
        if (blob.type && blob.type.startsWith('application/json')) {
          // Expected "no resolvable combo for this line item" state (no
          // triggering categories / no matching combo yet) -- the backend
          // now signals this with a 200 {"has_qr": false} JSON body instead
          // of a 404, so it's not a failure and shouldn't create an object
          // URL. No console noise for this case.
          setStatus('none');
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
        setStatus('ready');
      } catch (err: any) {
        if (cancelled) return;
        // Every caught error is now genuine (invalid order/item pair or an
        // admin-auth failure) -- the "no QR needed" case no longer throws,
        // so there's no expected-404 branch to swallow here anymore.
        console.error('Failed to load order item QR code image:', err);
        setStatus('error');
      }
    };

    loadQrImage();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [orderId, itemId]);

  if (status === 'loading') {
    return null;
  }

  if (status === 'none') {
    return <OrderItemQrPlaceholder>{t('admin.qbOrders.machineQrNoneNeeded')}</OrderItemQrPlaceholder>;
  }

  if (status === 'error') {
    return <OrderItemQrPlaceholder>{t('admin.qbOrders.machineQrLoadFailed')}</OrderItemQrPlaceholder>;
  }

  return (
    <OrderItemQrWrapper>
      <OrderItemQrLabel>{t('admin.qbOrders.machineQrLabel')}</OrderItemQrLabel>
      <OrderItemQrImage src={imageSrc} alt={t('admin.qbOrders.machineQrImageAlt')} />
    </OrderItemQrWrapper>
  );
};

// ─── Row component ────────────────────────────────────────────────────────────

interface OrderRowProps {
  order: QBOrder;
  onUpdateStatus: (id: number, status: string) => void;
  isUpdating: boolean;
}

const OrderRow: React.FC<OrderRowProps> = ({ order, onUpdateStatus, isUpdating }) => {
  const { t } = useTranslation();
  const [selectedStatus, setSelectedStatus] = useState(order.status);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'pending': return <FiClock size={12} />;
      case 'preparing': return <FiPackage size={12} />;
      case 'ready':
      case 'completed': return <FiCheck size={12} />;
      default: return null;
    }
  };

  return (
    <Tr>
      <Td>
        <QueueBadge>{formatQueueNumberValue(order.queue_number, t('admin.qbOrders.notAvailable'))}</QueueBadge>
      </Td>
      <Td>#{order.id}</Td>
      <Td>{order.customer_name ?? '—'}</Td>
      <Td>{order.customer_phone ?? '—'}</Td>
      <Td>
        {Array.isArray(order.items) ? order.items.map((item, idx) => {
          const parsedOpts: Record<string, string> = (item.options as Record<string, string>) ?? {};
          const hasOpts = Object.keys(parsedOpts).length > 0;
          return (
            <div key={idx}>
              <div>{item.menu_item_name ?? t('admin.qbOrders.itemFallback', { id: item.menu_item_id })} x{item.quantity}</div>
              {hasOpts && (
                <div style={{ fontSize: '11px', color: '#6B5E4E', marginTop: '2px' }}>
                  {Object.entries(parsedOpts).map(([k, v]) => `${k}: ${v}`).join(', ')}
                </div>
              )}
              {/* QB orders are only ever created after payment succeeds (see
                  bubbletea.py::create_order's synchronous Stripe confirm
                  before the row is written, and context.md's Discovered note
                  that QB order status never reflects payment state) -- so
                  every order here is already "paid" except a cancelled one,
                  which has nothing left to prepare/scan into the machine. */}
              {order.status !== 'cancelled' && (
                <OrderItemQrCell orderId={order.id} itemId={item.id} />
              )}
            </div>
          );
        }) : '—'}
      </Td>
      <Td>{formatCurrency(order.total)}</Td>
      <Td>
        <StatusBadge status={order.status}>
          {getStatusIcon(order.status)}
          {getStatusLabel(order.status, t)}
        </StatusBadge>
      </Td>
      <Td>{formatDate(order.created_at)}</Td>
      <Td>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <StatusSelect
            value={selectedStatus}
            onChange={e => setSelectedStatus(e.target.value)}
          >
            <option value="pending">{t('admin.qbOrders.statusOptionPending')}</option>
            <option value="preparing">{t('admin.qbOrders.statusOptionPreparing')}</option>
            <option value="ready">{t('admin.qbOrders.statusOptionReady')}</option>
            <option value="completed">{t('admin.qbOrders.statusOptionCompleted')}</option>
            <option value="cancelled">{t('admin.qbOrders.statusOptionCancelled')}</option>
          </StatusSelect>
          <UpdateButton
            onClick={() => onUpdateStatus(order.id, selectedStatus)}
            disabled={selectedStatus === order.status || isUpdating}
          >
            {t('admin.qbOrders.updateButton')}
          </UpdateButton>
        </div>
      </Td>
    </Tr>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const AdminQBOrders: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: rawOrders, isLoading } = useQuery({
    queryKey: ['adminQBOrders'],
    queryFn: fetchQBOrders,
  });
  const orders = Array.isArray(rawOrders) ? rawOrders : [];

  const updateStatusMutation = useMutation({
    mutationFn: updateQBOrderStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminQBOrders'] });
    },
  });

  const handleUpdateStatus = (id: number, status: string) => {
    updateStatusMutation.mutate({ id, status });
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.qbOrders.title')}</Title>
      </Header>

      {orders.length === 0 ? (
        <EmptyState>
          <FiPackage size={48} />
          <h3>{t('admin.qbOrders.emptyTitle')}</h3>
          <p>{t('admin.qbOrders.emptyDescription')}</p>
        </EmptyState>
      ) : (
        <Table>
          <Thead>
            <tr>
              <Th>{t('admin.qbOrders.queueHeader')}</Th>
              <Th>{t('admin.qbOrders.orderIdHeader')}</Th>
              <Th>{t('admin.qbOrders.customerNameHeader')}</Th>
              <Th>{t('admin.qbOrders.customerPhoneHeader')}</Th>
              <Th>{t('admin.qbOrders.itemsHeader')}</Th>
              <Th>{t('admin.qbOrders.totalHeader')}</Th>
              <Th>{t('admin.qbOrders.statusHeader')}</Th>
              <Th>{t('admin.qbOrders.createdAtHeader')}</Th>
              <Th>{t('admin.qbOrders.actionsHeader')}</Th>
            </tr>
          </Thead>
          <tbody>
            {orders.map(order => (
              <OrderRow
                key={order.id}
                order={order}
                onUpdateStatus={handleUpdateStatus}
                isUpdating={updateStatusMutation.isPending}
              />
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AdminQBOrders;
